import { useLayoutEffect, useRef, useState } from 'react';
import { Commentable } from '@/shared/comments/Commentable';
import { GlassCard, HelpAnchor, Pill, SurfacePageSectionHeader } from '@/design-system';
import { MeterBar } from '@/versions/v1/modules/_viz';
import type { AnalysisRunState } from '@/versions/v1/shared/useAnalysisRun';
import {
  analysisDiagnosisRoutes,
  analysisDiagnosisRows,
  type DiagnosisEvidenceView,
} from '@/versions/v1/modules/advisory/AnalysisRunDiagnosis.transform';
import { pluralCount } from '@/versions/v1/shared/text.derive';

function EvidenceLine({ item, against = false }: { item: DiagnosisEvidenceView; against?: boolean }) {
  return (
    <Pill tone={against ? 'warning' : 'default'} className="normal-case max-w-full whitespace-normal text-left">
      <span>{item.label}</span>
      <span className="ml-1 tabular-nums text-ink-primary">{item.valueLabel}</span>
    </Pill>
  );
}

function CandidateEvidence({
  label,
  evidence,
  against = false,
  compact = false,
}: {
  label: string;
  evidence: DiagnosisEvidenceView[];
  against?: boolean;
  compact?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [clipped, setClipped] = useState(false);
  const textRef = useRef<HTMLParagraphElement | null>(null);
  useLayoutEffect(() => {
    const node = textRef.current;
    if (!node || expanded) return undefined;
    const measure = () => setClipped(node.scrollHeight > node.clientHeight + 1);
    measure();
    // A live resize or the AI rail opening can clip the line after mount; re-measure on every size change.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(node);
    return () => observer?.disconnect();
  }, [expanded, evidence]);
  if (evidence.length === 0) return null;
  const visible = compact && !expanded ? evidence.slice(0, 1) : evidence;
  return (
    <div className={compact ? 'min-w-0' : 'space-y-1'}>
      {compact ? (
        <div className="flex min-w-0 items-end gap-1.5 text-[10px] leading-snug" data-candidate-evidence={against ? 'against' : 'for'}>
          <p ref={textRef} className={`min-w-0 flex-1 text-ink-primary ${expanded ? '' : 'line-clamp-2'}`}>
            <span className="mr-1.5 font-semibold uppercase tracking-wide text-muted">{label}</span>
            {visible.map((item) => (
              <span key={item.key} title={`${item.label}: ${item.valueLabel}`} className={expanded ? 'block' : undefined}>
                <span className="text-muted">{item.label}</span> · <span className="tabular-nums">{item.valueLabel}</span>
              </span>
            ))}
          </p>
          {evidence.length > 1 || clipped || expanded ? (
            <button type="button" className="shrink-0 whitespace-nowrap font-medium text-accent-700 hover:underline dark:text-accent-400" onClick={() => setExpanded((value) => !value)}>
              {expanded ? 'Show fewer' : evidence.length > 1 ? `+${pluralCount(evidence.length - 1, 'fact')}` : 'More'}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {visible.map((item) => (
            <EvidenceLine key={item.key} item={item} against={against} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AnalysisDiagnosisCandidateRow({
  row,
  rank,
  compact = false,
}: {
  row: ReturnType<typeof analysisDiagnosisRows>[number];
  /** Case-page list rank, printed in a leading gutter. */
  rank?: number;
  compact?: boolean;
}) {
  const statusPill = row.suggested ? (
    <Pill tone="success" className={compact ? 'shrink-0 px-1.5 text-[10px] normal-case tracking-normal' : 'shrink-0 normal-case'}>Suggested</Pill>
  ) : row.forReview ? (
    <Pill tone="warning" className={compact ? 'shrink-0 px-1.5 text-[10px] normal-case tracking-normal' : 'shrink-0 normal-case'}>For review</Pill>
  ) : null;
  const meter = (
    <MeterBar
      value={row.probability}
      max={1}
      tone={row.suggested ? 'ok' : row.forReview ? 'warn' : 'info'}
      label="Probability"
      valueLabel={row.probabilityLabel}
      markerCaption={false}
      className="space-y-1"
    />
  );
  if (compact) {
    return (
      <div
        className="grid grid-cols-[0.875rem_minmax(0,1fr)] gap-x-2 border-b border-[var(--hairline)] py-1.5 last:border-b-0 last:pb-0"
        data-candidate-row={row.causeId}
      >
        <span className={`pt-px text-xs font-semibold tabular-nums ${rank === 1 ? 'text-accent-700 dark:text-accent-400' : 'text-muted'}`}>{rank ?? ''}</span>
        <div className="min-w-0 space-y-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs font-semibold leading-snug text-ink-primary">{row.causeLabel}</p>
            {statusPill}
          </div>
          <div className="flex items-center gap-2" title={`Probability ${row.probabilityLabel}`}>
            <MeterBar
              value={row.probability}
              max={1}
              tone={row.suggested ? 'ok' : row.forReview ? 'warn' : 'info'}
              markerCaption={false}
              className="min-w-0 flex-1"
            />
            <span className="shrink-0 text-[10px] tabular-nums text-muted">{row.probabilityLabel}</span>
          </div>
          <CandidateEvidence label="For" evidence={row.evidenceFor} against={false} compact />
          <CandidateEvidence label="Against" evidence={row.evidenceAgainst} against compact />
        </div>
      </div>
    );
  }
  return (
    <GlassCard className="space-y-2 p-3" data-candidate-row={row.causeId}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-ink-primary">{row.causeLabel}</p>
        {statusPill}
      </div>
      {meter}
      <CandidateEvidence label="For" evidence={row.evidenceFor} against={false} />
      <CandidateEvidence label="Against" evidence={row.evidenceAgainst} against />
    </GlassCard>
  );
}

/** Ranked engine diagnosis selected from the same result as the drawer's replay frame. */
export function AnalysisRunDiagnosis({ run }: { run: AnalysisRunState }) {
  const [showAll, setShowAll] = useState(false);
  if (run.status !== 'ready') return null;

  const rows = analysisDiagnosisRows(run.result);
  if (rows.length === 0) return null;
  const visibleRows = showAll ? rows : rows.slice(0, 3);
  const hiddenCount = rows.length - visibleRows.length;
  const diagnosis = run.result.diagnosis;
  if (!diagnosis) return null;
  const routes = analysisDiagnosisRoutes(run.result);
  return (
    <Commentable name="Drawer.AnalysisRunDiagnosis">
      <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
        <SurfacePageSectionHeader
          title="Diagnosis"
          actions={<HelpAnchor doc="advisory/analysis-run" section="diagnosis" />}
        />
        <p className="text-xs text-muted">
          {diagnosis.candidates.length} {diagnosis.candidates.length === 1 ? 'cause' : 'causes'} · 100% total
        </p>
        {routes.controlPlanLabels.length > 0 || routes.tarpRoutes.length > 0 ? (
          <div className="space-y-1 border-b border-[var(--hairline)] pb-3 text-[11px] text-muted">
            {routes.controlPlanLabels.map((label) => (
              <p key={label}>{`Control Plan · ${label}`}</p>
            ))}
            {routes.tarpRoutes.map((route) => (
              <p key={route.key}>
                {route.label}
                <span>{` · ${route.causeLabels.join('; ')}`}</span>
              </p>
            ))}
          </div>
        ) : null}
        <div className="space-y-2">
          {visibleRows.map((row) => (
            <AnalysisDiagnosisCandidateRow key={row.causeId} row={row} />
          ))}
        </div>
        {rows.length > 3 ? (
          <button
            type="button"
            className="text-xs font-medium text-accent-700 hover:underline dark:text-accent-400"
            onClick={() => setShowAll((current) => !current)}
          >
            {showAll ? 'Show top 3 causes' : `Show ${hiddenCount} more causes`}
          </button>
        ) : null}
      </section>
    </Commentable>
  );
}
